import { PlusIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import OrganizationAddressFormModal from 'src/features/organization/organization-address/ui/OrganizationAddressFormModal';
import { AddressBlock } from 'src/shared/components';
import { Address, Organization } from 'src/shared/types/user';

import { useDisclosure } from 'src/shared/components/ui';
import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  Typography
} from 'src/shared/components/ui';

interface AddressSelectionModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onConfirm: (address: Address) => void;
  title?: string;
  confirmLabel?: string;
  isLoading?: boolean;
}

const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
  title = 'Select shipping address',
  confirmLabel = 'Confirm',
  isLoading = false
}) => {
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const {
    isOpen: isOpenAddress,
    onOpen: onOpenAddress,
    onOpenChange: onOpenChangeAddress
  } = useDisclosure();
  const [selected, setSelected] = useState(
    new Set(
      selectedOrganization?.addresses
        ? [selectedOrganization?.addresses[0]?.name]
        : []
    )
  );
  const [isOpenSelect, setIsOpenSelect] = useState(false);
  const [editingAddress, setEditingAddress] = useState<
    (Address & { index: number }) | null
  >(null);

  const addressOptions = useMemo(() => {
    return (
      selectedOrganization?.addresses?.map((address, index) => ({
        key: address.name,
        label: address.name,
        index,
        ...address
      })) || []
    );
  }, [selectedOrganization]);

  const handleSelectionChange = (val: Set<string>) => {
    if (val.has('new')) {
      onOpenAddress();
    } else {
      if (val.size > 0) {
        setSelected(new Set(val));
      }
    }
  };

  const handleConfirm = () => {
    const selectedAddressName = Array.from(selected)[0];

    if (!selectedAddressName) return;

    const address = selectedOrganization?.addresses?.find(
      (addr) => addr.name === selectedAddressName
    );

    if (!address) return;

    onConfirm(address);
  };

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="lg">
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <Typography variant="h4" weight="bold">
                  {title}
                </Typography>
              </ModalHeader>
              <ModalBody>
                <Select
                  isOpen={isOpenSelect}
                  setIsOpen={setIsOpenSelect}
                  fullWidth
                  disallowEmptySelection
                  selectedKeys={selected}
                  onSelectionChange={handleSelectionChange}
                  label="Ship to"
                  size="lg"
                  items={[
                    ...addressOptions,
                    { label: 'Use new address', key: 'new' }
                  ]}
                  renderValue={(items: any) => {
                    return items.map((item: any) => {
                      const address = addressOptions.find(
                        (addr) => addr.key === item.key
                      );

                      return (
                        <div
                          className="flex items-center gap-1"
                          key={address?.key}
                        >
                          <Typography variant="body-1">
                            {address?.key}
                          </Typography>
                          <Typography
                            variant="body-1"
                            className="text-secondary-text"
                          >
                            {address?.line_1}, {address?.line_2},{' '}
                            {address?.state}, {address?.city}{' '}
                            {address?.zip_code} {address?.country}
                          </Typography>
                        </div>
                      );
                    });
                  }}
                  customComponent={(item: any) => {
                    if (item.key === 'new') {
                      return (
                        <div>
                          <Typography
                            variant="body-1"
                            weight="medium"
                            className="text-primary flex items-center gap-1"
                          >
                            <PlusIcon className="size-5" /> Use new address
                          </Typography>
                        </div>
                      );
                    }

                    return (
                      <>
                        <AddressBlock address={item} />
                        <Typography
                          onClick={() => {
                            setEditingAddress(item);
                            setIsOpenSelect(false);
                            onOpenChangeAddress();
                          }}
                          variant="body-1"
                          className="text-primary"
                        >
                          Edit address
                        </Typography>
                      </>
                    );
                  }}
                />
              </ModalBody>
              <ModalFooter>
                <ButtonV2
                  color="default"
                  variant="light"
                  onPress={() => onOpenChange(false)}
                >
                  Cancel
                </ButtonV2>
                <ButtonV2
                  onPress={handleConfirm}
                  isDisabled={selected.size === 0 || isLoading}
                  isLoading={isLoading}
                >
                  {confirmLabel}
                </ButtonV2>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <OrganizationAddressFormModal
        isOpen={isOpenAddress}
        onOpenChange={onOpenChangeAddress}
        selectedAddress={editingAddress}
        selectedOrganization={selectedOrganization as Organization}
        onSuccess={(name) => {
          setTimeout(() => {
            handleSelectionChange(new Set([name]));
          }, 100);
        }}
      />
    </>
  );
};

export default AddressSelectionModal;
